import { BoundingInfo, Geometry, TmpVectors, VertexBuffer } from "@babylonjs/core";
import { GLTFLoader, ArrayItem } from "@babylonjs/loaders/glTF/2.0";
const NAME = "EXT_mesh_features";
export function HasFeatureIds(b) {
    if (b === null || b === undefined || typeof b !== "object")
        return false;
    const obj = b;
    return obj.featureIds !== undefined && Array.isArray(obj.featureIds) && obj.featureIds.length != 0;
}
export class EXT_mesh_features {
    static BuildKind(prefix, n) {
        return `${prefix}${n}`;
    }
    constructor(loader) {
        this.name = NAME;
        this._loader = loader;
        this.enabled = this._loader.isExtensionUsed(NAME);
    }
    dispose() {
        this._loader = null;
    }
    _loadVertexDataAsync(context, primitive, babylonMesh) {
        if (primitive.extensions) {
            const attributes = primitive.attributes;
            if (!attributes) {
                throw new Error(`${context}: Attributes are missing`);
            }
            const babylonGeometry = new Geometry(babylonMesh.name, this._loader._babylonScene);
            const promises = new Array();
            if (primitive.indices == undefined) {
                babylonMesh.isUnIndexed = true;
            }
            else {
                const accessor = ArrayItem.Get(`${context}/indices`, this._loader._gltf.accessors, primitive.indices);
                promises.push(this._loader._loadIndicesAccessorAsync(`/accessors/${accessor.index}`, accessor).then((data) => {
                    babylonGeometry.setIndices(data);
                }));
            }
            const loadAttribute = (name, kind, callback) => {
                if (attributes[name] == undefined) {
                    return;
                }
                babylonMesh._delayInfo = babylonMesh._delayInfo || [];
                if (babylonMesh._delayInfo.indexOf(kind) === -1) {
                    babylonMesh._delayInfo.push(kind);
                }
                const accessor = ArrayItem.Get(`${context}/attributes/${name}`, this._loader._gltf.accessors, attributes[name]);
                promises.push(this._loader._loadVertexAccessorAsync(`/accessors/${accessor.index}`, accessor, kind).then((babylonVertexBuffer) => {
                    if (babylonVertexBuffer.getKind() === VertexBuffer.PositionKind && !this._loader.parent.alwaysComputeBoundingBox && !babylonMesh.skeleton) {
                        if (accessor.min && accessor.max) {
                            const min = TmpVectors.Vector3[0].copyFromFloats(...accessor.min);
                            const max = TmpVectors.Vector3[1].copyFromFloats(...accessor.max);
                            if (accessor.normalized && accessor.componentType !== 5126) {
                                let divider = 1;
                                switch (accessor.componentType) {
                                    case 5120:
                                        divider = 127.0;
                                        break;
                                    case 5121:
                                        divider = 255.0;
                                        break;
                                    case 5122:
                                        divider = 32767.0;
                                        break;
                                    case 5123:
                                        divider = 65535.0;
                                        break;
                                }
                                const oneOverDivider = 1 / divider;
                                min.scaleInPlace(oneOverDivider);
                                max.scaleInPlace(oneOverDivider);
                            }
                            babylonGeometry._boundingInfo = new BoundingInfo(min, max);
                            babylonGeometry.useBoundingInfoFromGeometry = true;
                        }
                    }
                    babylonGeometry.setVerticesBuffer(babylonVertexBuffer, accessor.count);
                }));
                if (kind == VertexBuffer.MatricesIndicesExtraKind) {
                    babylonMesh.numBoneInfluencers = 8;
                }
                if (callback) {
                    callback(accessor);
                }
            };
            const attributeMappings = [
                ["TEXCOORD_0", VertexBuffer.UVKind, null],
                ["TEXCOORD_1", VertexBuffer.UV2Kind, null],
                ["TEXCOORD_2", VertexBuffer.UV3Kind, null],
                ["TEXCOORD_3", VertexBuffer.UV4Kind, null],
                ["TEXCOORD_4", VertexBuffer.UV5Kind, null],
                ["TEXCOORD_5", VertexBuffer.UV6Kind, null],
                ["POSITION", VertexBuffer.PositionKind, null],
                ["NORMAL", VertexBuffer.NormalKind, null],
                ["TANGENT", VertexBuffer.TangentKind, null],
                ["JOINTS_0", VertexBuffer.MatricesIndicesKind, null],
                ["WEIGHTS_0", VertexBuffer.MatricesWeightsKind, null],
                ["JOINTS_1", VertexBuffer.MatricesIndicesExtraKind, null],
                ["WEIGHTS_1", VertexBuffer.MatricesWeightsExtraKind, null],
                [
                    "COLOR_0",
                    VertexBuffer.ColorKind,
                    (accessor) => {
                        if (accessor.type === "VEC4") {
                            babylonMesh.hasVertexAlpha = true;
                        }
                    },
                ],
            ];
            const extension = primitive.extensions[EXT_mesh_features.name];
            if (extension && extension.featureIds) {
                const babylonObject = babylonMesh;
                const featureIds = (babylonObject.featureIds = babylonObject.featureIds ?? []);
                for (const i of extension.featureIds) {
                    featureIds.push(i);
                }
                let vfidCount = 0;
                const implicit = [];
                for (const fid of featureIds) {
                    if (fid.attribute != undefined) {
                        const n = EXT_mesh_features.BuildKind("_FEATURE_ID_", fid.attribute);
                        fid.vertexAttributeKind = EXT_mesh_features.BuildKind(EXT_mesh_features.VerticeKindPrefix, fid.attribute);
                        attributeMappings.push([n, fid.vertexAttributeKind, null]);
                        vfidCount++;
                        continue;
                    }
                    if (fid.texture?.texCoord != undefined) {
                        fid.vertexAttributeKind = EXT_mesh_features.BuildKind(EXT_mesh_features.uvKindPrefix, fid.texture?.texCoord);
                        this._loader.loadTextureInfoAsync(context, fid.texture).then((babylonTexture) => {
                            fid.textureData = babylonTexture;
                        });
                        continue;
                    }
                    implicit.push(fid);
                }
                for (const fid of implicit) {
                    fid.vertexAttributeKind = EXT_mesh_features.BuildKind(EXT_mesh_features.VerticeKindPrefix, vfidCount++);
                    const buffer = this._buildVertexBufferForImplicitId(fid.featureCount, fid.vertexAttributeKind);
                    babylonGeometry.setVerticesBuffer(buffer, fid.featureCount);
                }
            }
            attributeMappings.forEach(([attributeName, vertexKind, callback]) => {
                loadAttribute(attributeName, vertexKind, callback == null ? undefined : callback);
            });
            return Promise.all(promises).then(() => {
                return babylonGeometry;
            });
        }
        return null;
    }
    _buildVertexBufferForImplicitId(count, kind) {
        const generatedIndices = Array.from({ length: count }, (_, i) => i);
        const engine = this._loader._babylonScene.getEngine();
        return new VertexBuffer(engine, generatedIndices, kind, false, undefined, 4, false, 0, 1, 5126);
    }
}
EXT_mesh_features.VerticeKindPrefix = "fid";
EXT_mesh_features.uvKindPrefix = "uv";
GLTFLoader.RegisterExtension(NAME, (loader) => new EXT_mesh_features(loader));
//# sourceMappingURL=EXT_mesh_features.js.map